import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Typography,
  Chip,
} from '@mui/material';
import { useConditionStore } from '../store/conditionStore';
import { RULE_TYPE_LABELS } from '../utils/constants';
import type { ParsedCondition } from '../types';

/**
 * AI 自然语言解析输入框
 * 用户输入自然语言条件 → AI 解析为结构化规则 → 预览 → 保存
 */
export default function AiParseInput() {
  const [text, setText] = useState('');
  const { parseCondition, parsing, parsedResult, error, clearParsed } = useConditionStore();

  const handleParse = async () => {
    if (!text.trim()) return;
    await parseCondition(text);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          AI 智能解析条件
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder='用自然语言描述条件，如"连续5天换手率大于10%"'
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleParse();
            }}
          />
          <Button
            variant="contained"
            onClick={handleParse}
            disabled={parsing || !text.trim()}
            startIcon={parsing ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {parsing ? '解析中...' : 'AI解析'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}

        {parsedResult && (
          <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                解析结果预览
              </Typography>
              <Chip
                size="small"
                label={`置信度 ${(parsedResult.confidence * 100).toFixed(0)}%`}
                color={parsedResult.confidence > 0.7 ? 'success' : 'warning'}
              />
            </Box>
            <ParsedConditionView result={parsedResult} />
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" color="primary">
                确认保存
              </Button>
              <Button size="small" onClick={clearParsed}>
                取消
              </Button>
            </Box>
          </Paper>
        )}
      </Paper>
    </Box>
  );
}

/**
 * 展示解析结果
 */
function ParsedConditionView({ result }: { result: ParsedCondition }) {
  return (
    <Box sx={{ fontSize: 13, lineHeight: 1.8 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ width: 80 }}>
          规则类型:
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {RULE_TYPE_LABELS[result.ruleType] || result.ruleType}
        </Typography>
      </Box>
      {result.value && (
        <Box sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ width: 80 }}>
            阈值:
          </Typography>
          <Typography variant="body2">
            {result.operator} {result.value}
          </Typography>
        </Box>
      )}
      {Object.keys(result.params).length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ width: 80 }}>
            参数:
          </Typography>
          <Typography variant="body2">
            {JSON.stringify(result.params)}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ width: 80 }}>
          描述:
        </Typography>
        <Typography variant="body2">{result.description}</Typography>
      </Box>
    </Box>
  );
}
